import React from 'react';
import cx from 'classnames';
// components
import Theme from './Theme.js';
import ValuePropositions from './ValuePropositions.js';
import LinkAuthFlow from './LinkAuthFlow.js';
import Link from './Link.js';
// hooks
import useSearchParamOrDefaultProps from '../hooks/useSearchParamOrDefaultProps.js';
// styles
import styles from './GateArticle.module.css';
import Gate from './Gate.js';
// icons + logos
import LogoPro from './LogoPro.js';
import LogoInvestingClub from './LogoInvestingClub.js';
import LogoBrand from './Logo.js';
import IconNewsletter from './IconNewsletter.js';
import IconWatchlist from './IconWatchlist.js';
import IconTrustPortfolio from './IconTrustPortfolio.js';
import IconLiveMeeting from './IconMeeting.js';
import IconExpertAccess from './IconExpertAccess.js';
import IconDailyCheck from './IconDailyCheck.js';
import IconMarketNews from './IconMarketNews.js';
import IconInvestingTools from './IconInvestingTools.js';
import IconLivestream from './IconLivestream.js';
import IconAccess from './IconAccess.js';

/**
 * GateArticle: A component for displaying a Article Gate, a paywall for articles that requires a subscription.
 * @component
 * @returns {JSX.Element} The rendered JSX element.
 */
const GateArticle = () => {
  const {
    theme: themeParam,
    headerTitle,
    primaryButtonText,
    primaryButtonLink,
    secondaryButtonText,
    secondaryButtonLink,
    hideSecondaryButton,
    showValuePropsList: showValuePropsParam,
    valuePropTitle,
    product,
  } = useSearchParamOrDefaultProps();

  // convert showValueProps to proper boolean type
  const showValueProps = showValuePropsParam === 'true';

  const themes = {
    default: {
      brand: 'default',
      colorTheme: '#001e5a',
      valuePropItems: [
        { text: 'Create watchlists', icon: <IconWatchlist /> },
        { text: 'Exclusive Newsletters', icon: <IconNewsletter /> },
      ],
      logo: <LogoBrand logoMaxWidthProp={'90px'} />,
    },
    pro: {
      brand: 'pro',
      colorTheme: '#047E2E',
      valuePropItems: [
        { text: 'Market News & Analysis', icon: <IconMarketNews /> },
        { text: 'Essential Investing Tools', icon: <IconInvestingTools /> },
        { text: 'Stream CNBC Live', icon: <IconLivestream /> },
        { text: 'Exclusive Access to Experts', icon: <IconExpertAccess /> },
      ],
      logo: <LogoPro />,
    },
    investingClub: {
      brand: 'investingClub',
      colorTheme: '#0053CF',
      valuePropItems: [
        { text: 'Jim Cramer Trust Portfolio', icon: <IconTrustPortfolio /> },
        { text: 'Live Meetings & Events', icon: <IconLiveMeeting /> },
        { text: 'Access Anywhere', icon: <IconAccess /> },
        { text: 'Check In Daily with Jim', icon: <IconDailyCheck /> },
      ],
      logo: <LogoInvestingClub />,
    },
  };

  const theme = themes[themeParam] || themes['default'];

  const { brand, colorTheme, valuePropItems, logo } = theme;

  const isRegularUser = brand === 'default';
  const brandColor = { color: colorTheme, borderColor: colorTheme };
  const brandBtnColor = { backgroundColor: colorTheme };
  const brandBorderColor = { color: colorTheme, borderColor: colorTheme };

  return (
    <Theme>
      <Gate>
        <section
          className={cx(styles.gateSection, {
            [styles.verticalLine]: !showValueProps,
          })}
        >
          <header className={styles.topNavBar}>
            <div>{logo}</div>
            <LinkAuthFlow
              color={brandColor.color}
              eventType={'phx_zephr_click_sign_in'}
              fontWeight={600}
              alignSelf={'start'}
              text={'Sign in'}
              fontSize={'14px'}
              textTransform={'uppercase'}
              product={product}
              modalType={'signIn'}
              contentType="article"
            >
              Sign In
            </LinkAuthFlow>
          </header>
          <div
            className={cx(styles.contentContainer, {
              [styles.horizontalLine]: !showValueProps,
            })}
          >
            <div className={cx(styles.headlineSection)}>
              <h1 className={styles.headerTitle}>{headerTitle}</h1>
              {showValueProps && (
                <div className={styles.valuePropsContainer}>
                  <h2 className={styles.valuePropsTitle}>{valuePropTitle}</h2>
                  <div
                    style={brandColor}
                    className={styles.valuePropsMobileWrapper}
                  >
                    <ValuePropositions items={valuePropItems} />
                  </div>
                </div>
              )}
              <div
                className={cx(styles.buttonGroup, {
                  [styles.buttonGroupNoValueProps]: !showValueProps,
                })}
              >
                <div
                  className={cx(styles.buttonGroupPrimaryBtn, {
                    [styles.buttonGroupPrimaryBtnValueProps]: showValueProps,
                  })}
                >
                  <LinkAuthFlow
                    padding={'15px 25px'}
                    color={'white'}
                    backgroundColor={brandBtnColor.backgroundColor}
                    borderWidth={'1px'}
                    borderStyle={'solid'}
                    borderColor={brandBorderColor.borderColor}
                    borderRadius={'3px'}
                    margin={'0 0 10px 0'}
                    whiteSpace={'nowrap'}
                    fontWeight={600}
                    fontFamily={'proxima-nova, sans-serif'}
                    eventType={'phx_zephr_click_call_to_action'}
                    to={primaryButtonLink}
                    text={primaryButtonText}
                    textTransform={'uppercase'}
                    product={product}
                    modalType={'signUp'}
                    contentType="article"
                  >
                    {primaryButtonText}
                  </LinkAuthFlow>
                </div>

                {hideSecondaryButton !== 'true' && (
                  <div
                    className={cx(styles.buttonGroupSecondaryBtn, {
                      [styles.buttonGroupSecondaryBtnValueProps]:
                        showValueProps,
                    })}
                  >
                    <Link
                      borderWidth={'1px'}
                      borderStyle={'solid'}
                      borderColor={brandBorderColor.borderColor}
                      borderRadius={'3px'}
                      padding={'15px 25px'}
                      margin={'0 0 50px 0'}
                      color={brandBorderColor.color}
                      whiteSpace={'nowrap'}
                      fontWeight={600}
                      fontFamily={'proxima-nova, sans-serif'}
                      eventType={'phx_zephr_click_learn_more'}
                      to={secondaryButtonLink}
                      text={secondaryButtonText}
                      textTransform={'uppercase'}
                    >
                      {secondaryButtonText}
                    </Link>
                  </div>
                )}
              </div>
            </div>
            <main
              className={cx(styles.mainContent, styles.mainValueProps, {
                [styles.hideContentBlock]: !showValueProps,
              })}
            >
              {showValueProps && (
                <div className={styles.valuePropsTitleDesktop}>
                  <h2>{valuePropTitle}</h2>
                  <div style={brandColor}>
                    <ValuePropositions items={valuePropItems} />
                  </div>
                </div>
              )}
              {showValueProps && (
                <div className={styles.secondaryButtonWrapper}>
                  <Link
                    borderWidth={'1px'}
                    borderStyle={'solid'}
                    borderColor={brandBorderColor.borderColor}
                    borderRadius={'3px'}
                    padding={'17px 26px'}
                    margin={'0 0 10px 0'}
                    color={brandBorderColor.color}
                    whiteSpace={'nowrap'}
                    fontWeight={600}
                    fontFamily={'proxima-nova, sans-serif'}
                    eventType={'phx_zephr_click_learn_more'}
                    to={secondaryButtonLink}
                    text={secondaryButtonText}
                    textTransform={'uppercase'}
                  >
                    {secondaryButtonText}
                  </Link>
                </div>
              )}
            </main>
          </div>
        </section>
      </Gate>
    </Theme>
  );
};

GateArticle.displayName = 'GateArticle';

export default GateArticle;
